/**
 * StripeNativePaymentSheet (T35.7.2.2 — native checkout) — the OS Google/Apple
 * Pay "Pay" button shown ABOVE the always-rendered web card form on the installed
 * Capacitor app.
 *
 * This is an ADDITIVE upgrade, not a replacement. On the Android app Stripe's web
 * wallet button does not work (it rides the browser Payment Request API,
 * unreliable in the System WebView), so when the wallet can genuinely run this
 * component presents the OS Google/Apple Pay sheet via the native Stripe SDK
 * (`@capacitor-community/stripe`, behind the `stripeNative` seam). It is composed
 * INSIDE the Stripe surface above `<StripePaymentElement>` — `RENDER_ADAPTERS`
 * still keys only on `descriptor.processor`, and the card form is always present
 * beneath it (the parent renders it unconditionally).
 *
 * Same money path. The sheet charges against the SAME session client secret
 * (`descriptor.primary.sessionToken`) and the row finalizes from the SAME webhook
 * — a `completed` sheet is charged-at-Stripe-but-still-pending, so `onResult(true)`
 * fires exactly as the web confirm does and the success UI reads PENDING (never an
 * immediate "backed!"). Card data is tokenized in the OS wallet — never our DOM
 * (PCI SAQ-A); this component never writes `subscriptions`.
 *
 * No dead-end on cancel/fail. A `canceled` sheet stays on the action (the user
 * dismissed on purpose). A `failed` sheet shows a brief inline retryable message
 * — it does NOT hide anything; the card form below remains available so the user
 * can always still pay.
 *
 * Token-only styling (CP5); mobile-first; ≥44px targets.
 */

import { type CSSProperties, useState } from 'react';
import type { SessionDescriptor } from './api';
import {
  presentStripeNativeSheet,
  type StripeNativeSheetResult,
} from '../../adapters/stripeNative';
import { Spinner } from '../../components/Spinner';

// The Google Pay checkout button (BUG36). Renders Google's OFFICIAL full Google
// Pay button — the complete button (pill surface + "Google Pay" lockup), NOT a
// bare mark on an app surface — to satisfy Google's web payment-button brand
// guidelines (their production review rejected the old mark-only custom button).
// Google's rule is explicit: don't create your own button or alter its font,
// colour, radius, or padding. So the official asset IS the button: the element
// itself carries only structural style (full width, the touch-floor height,
// centering, the top clear space) — no app background, border, radius, font, or
// padding that would "alter the official button".
//
// Theme-correct variant: the BLACK official button under the light theme and the
// WHITE official button under the dark theme (Google's contrast guideline), driven
// off the single app-wide `[data-theme]` switch — both SVGs are mounted and the
// CSS rule in responsive.css shows the right one (no per-component theme read).
//
// The only Google brand colours live inside the two `.svg` assets (the existing
// CP5 vendor-brand exception — token-scan does not inspect `.svg`); no Google hex
// is hardcoded in this `.tsx`/`.css`. The spinner state swaps the official button
// for the app's accent spinner so the in-flight cue is unmistakable.
const payBtnStyle: CSSProperties = {
  marginTop: 'var(--space-3)', // ≥var(--space-3) (12px ≥ 8dp) top clear space
  width: '100%',
  height: '3rem', // 48px resting height (proportional to adjacent controls)
  minHeight: '2.75rem', // ≥44px touch target floor (frontend-standards §3)
  padding: 0,
  border: 0,
  background: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// The official Google Pay button asset fills the button height and centers; width
// follows the SVG's intrinsic aspect ratio so the lockup never stretches. The
// official button's own corner radius / surface / padding come from the asset —
// we apply none of our own (that would alter the official button).
//
// `display` is deliberately NOT set here. Both theme variants are mounted; the
// SINGLE source of truth for which one shows is the responsive.css `[data-theme]`
// rule. An inline `display` would win the cascade and force BOTH variants visible
// (BUG36: the button rendered twice, black AND white side by side). Sizing only.
const payMarkStyle: CSSProperties = {
  height: '100%',
  width: 'auto',
  flexShrink: 0,
};

const errorStyle: CSSProperties = {
  marginTop: 'var(--space-2)',
  color: 'var(--color-feedback-error)',
  fontSize: 'var(--font-size-xs)',
};

/**
 * Whether the native shell is running against a Stripe test key, so the Google
 * Pay sheet runs in test mode. The publishable key carries the mode in its prefix
 * (`pk_test_…` vs `pk_live_…`).
 */
function isStripeTestMode(): boolean {
  const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined;
  return Boolean(key && key.startsWith('pk_test_'));
}

/**
 * The native Stripe "Pay" button. Renders a "Pay" action; on tap it presents the
 * OS wallet sheet against the session client secret and maps the result up. A
 * `completed` sheet reports `onResult(true)` (charged, still pending until the
 * webhook); `canceled` leaves the user on the action (no error).
 *
 * No dead-end on `failed`. The probe upstream only proves the wallet COULD run; if
 * the sheet actually fails at present time (init throws, the sheet errors), the
 * result is `failed` and this button shows a brief inline retryable message. It
 * does NOT hide anything — the always-rendered web card form below (owned by the
 * parent) lets the user still pay with a card. A user `canceled` is NOT a failure
 * (they dismissed on purpose) — that stays on the native action, no message.
 *
 * `presentSheet` is injectable so the RTL test drives the sheet path with a fake
 * (no device, no real plugin); the default wires the real `stripeNative` seam.
 */
/** The generic Google Pay failure reason carried up when the sheet errors at present time. */
const NATIVE_FAILURE_REASON = 'Google Pay couldn’t be completed. Try again, or pay by card.';

export function StripeNativePaymentSheet({
  descriptor,
  onResult,
  presentSheet = presentStripeNativeSheet,
  merchantDisplayName,
}: {
  descriptor: SessionDescriptor;
  onResult?: ((ok: boolean, reason?: string) => void) | undefined;
  /** Seam override (tests inject a fake); defaults to the real native sheet. */
  presentSheet?: (opts: {
    clientSecret: string;
    merchantDisplayName?: string | undefined;
    googlePayIsTesting?: boolean | undefined;
  }) => Promise<StripeNativeSheetResult>;
  /** Shown on the OS wallet sheet (e.g. the backed idea); receipts name it server-side. */
  merchantDisplayName?: string;
}): JSX.Element {
  const clientSecret = descriptor.primary.sessionToken;
  const [submitting, setSubmitting] = useState(false);
  // Set when the native sheet FAILS at present time (not a user cancel). Shows a
  // brief retryable message beneath the button — it never hides the card form,
  // which the parent always renders below this button.
  const [failed, setFailed] = useState(false);

  async function pay(): Promise<void> {
    if (!clientSecret || submitting) return;
    setSubmitting(true);
    setFailed(false);
    const result = await presentSheet({
      clientSecret,
      merchantDisplayName,
      googlePayIsTesting: isStripeTestMode(),
    });
    setSubmitting(false);
    if (result === 'completed') {
      // Charged at Stripe — the row is still `recorded` until the webhook lands,
      // so the caller's pending UI shows, never an immediate success.
      onResult?.(true);
      return;
    }
    if (result === 'canceled') {
      // User dismissed the wallet sheet — no error, stay on the pay action.
      // NOT a failure: don't carry a reason up, don't show the dialog's failure
      // message. The user chose to back out; leave them on the Pay action.
      return;
    }
    // The native sheet failed at runtime (not a user cancel). Surface a brief
    // inline message AND carry the failure up so the dialog shows message 2 —
    // the always-present card form below still lets them pay.
    setFailed(true);
    onResult?.(false, NATIVE_FAILURE_REASON);
  }

  return (
    <div data-testid="render-adapter-stripe-native" data-session-token={clientSecret ?? ''}>
      <button
        type="button"
        style={payBtnStyle}
        data-testid="stripe-native-pay"
        aria-label="Pay with Google Pay"
        disabled={!clientSecret || submitting}
        aria-busy={submitting || undefined}
        onClick={pay}
      >
        {submitting ? (
          <>
            <Spinner
              size="sm"
              color="var(--color-text-primary)"
              label="Processing"
              data-testid="stripe-native-pay-spinner"
            />
            {/* Keep the text cue ("Processing…") next to the spinner so the wait
                is still conveyed under reduced motion / to assistive tech. */}
            Processing…
          </>
        ) : (
          // The official Google Pay button — the recognizable primary action. Both
          // theme variants are mounted; the responsive.css `[data-theme]` rule
          // shows the black button under light and the white button under dark.
          // The accessible name comes from the button's aria-label; the asset is
          // decorative (aria-hidden) so the name is not doubled.
          <span
            data-testid="stripe-native-pay-mark"
            style={{ display: 'contents' }}
            aria-hidden="true"
          >
            <img
              src="/icons/google-pay-button-dark.svg"
              alt=""
              aria-hidden="true"
              className="gpay-btn-img-dark"
              style={payMarkStyle}
            />
            <img
              src="/icons/google-pay-button-light.svg"
              alt=""
              aria-hidden="true"
              className="gpay-btn-img-light"
              style={payMarkStyle}
            />
          </span>
        )}
      </button>
      {failed && (
        <div role="alert" style={errorStyle} data-testid="stripe-native-pay-error">
          Google Pay couldn’t be completed. Try again, or pay by card below.
        </div>
      )}
    </div>
  );
}
